import React, { useEffect, useRef, useState } from "react";
import { MdAttachFile, MdClose } from "react-icons/md";
import { tamanhoLegivel } from "../../utils/formatStringUtils";
import { adicionarAnexos, arquivosDaColagem } from "./anexosRegras";

interface Props {
  value: File[];
  onChange: (arquivos: File[]) => void;
  disabled?: boolean;
  /** Campo de texto do compositor: colar um print dentro dele vira anexo (o evento sobe até aqui). */
  children?: React.ReactNode;
}

/**
 * Anexos de um envio: clipe, arrastar e soltar sobre a área e colar imagem. As regras (5 arquivos,
 * 10 MB cada, 25 MB somados, extensões bloqueadas) são checadas aqui para o erro aparecer antes do
 * envio; o servidor repete a checagem.
 */
const AnexosInput: React.FC<Props> = ({ value, onChange, disabled, children }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [arrastando, setArrastando] = useState(false);

  // Sem anexos não há o que o erro antigo explique (ex.: formulário resetado).
  useEffect(() => {
    if (value.length === 0) setErro(null);
  }, [value.length]);

  const adicionar = (novos: File[]) => {
    if (novos.length === 0) return;
    const r = adicionarAnexos(value, novos);
    setErro(r.erro);
    if (!r.erro) onChange(r.arquivos);
  };

  const remover = (i: number) => {
    setErro(null);
    onChange(value.filter((_, idx) => idx !== i));
  };

  const onPaste = (e: React.ClipboardEvent) => {
    if (disabled) return;
    const arquivos = arquivosDaColagem(e.clipboardData);
    if (arquivos.length === 0) return; // texto comum: deixa o navegador colar
    e.preventDefault();
    adicionar(arquivos);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setArrastando(false);
    if (disabled) return;
    adicionar(Array.from(e.dataTransfer.files));
  };

  return (
    <div
      onPaste={onPaste}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setArrastando(true);
      }}
      onDragLeave={(e) => {
        // Passar por cima de um filho também dispara dragleave: só desliga ao sair do container.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setArrastando(false);
      }}
      onDrop={onDrop}
      className={`rounded-xl transition-colors ${
        arrastando ? "ring-2 ring-dv-blue-500 dark:ring-dv-cyan-400" : ""
      }`}
    >
      {children}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          aria-label="Anexar arquivos"
          title="Anexar arquivos (ou arraste, ou cole um print)"
          className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 transition-all hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-navy-900 dark:text-gray-200 dark:hover:bg-navy-700"
        >
          <MdAttachFile className="h-4 w-4" aria-hidden="true" />
          Anexar
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          data-testid="anexos-file-input"
          onChange={(e) => {
            adicionar(Array.from(e.target.files ?? []));
            e.target.value = ""; // permite escolher o mesmo arquivo de novo após remover
          }}
        />
        <span className="text-[11px] text-gray-500 dark:text-gray-400">
          Arraste arquivos ou cole um print aqui.
        </span>

        {value.map((f, i) => (
          <span
            key={`${f.name}-${i}`}
            className="inline-flex max-w-[16rem] items-center gap-1.5 rounded-full bg-gray-100 py-1 pl-2.5 pr-1 text-xs text-gray-800 dark:bg-navy-700 dark:text-gray-100"
          >
            <span className="truncate" title={f.name}>
              {f.name}
            </span>
            <span className="shrink-0 text-gray-500 dark:text-gray-400">{tamanhoLegivel(f.size)}</span>
            <button
              type="button"
              onClick={() => remover(i)}
              disabled={disabled}
              aria-label={`Remover ${f.name}`}
              className="rounded-full p-0.5 text-gray-600 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500 dark:text-gray-300 dark:hover:bg-navy-600"
            >
              <MdClose className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </span>
        ))}
      </div>

      {erro && (
        <p role="alert" className="mt-1.5 text-xs font-semibold text-red-600 dark:text-red-400">
          {erro}
        </p>
      )}
    </div>
  );
};

export default AnexosInput;
